import React from 'react';
import PropTypes from 'prop-types';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Row, Col, Input } from 'antd';
import { getSafeHtml } from 'common/helpers/safeHtml';

const inputTypes = {
  NUMBER: 'number', // only number
  NUMBER_LETTER: 'number_and_letter', // only number & letter
  LETTER: 'letter', // only letter
  FREE: 'free', // free text
  NUMBER_LETTER_SPACE: 'number_letter_space', // only number, letter and space
  NO_NUMBER: 'no_number', // no number characters
};

export const bankTransferFields = {
  uk: {
    nameOfBeneficiary: {
      type: inputTypes.NUMBER_LETTER_SPACE,
      transKey: 'name_of_beneficiary',
      required: true,
    },
    sortCode: {
      type: inputTypes.NUMBER,
      transKey: 'sort_code',
      required: true,
    },
    accountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'account_number',
      required: true,
    },
    ibanNumber: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'iban_number',
      required: true,
    },
    bicOrSwiftCode: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'bic_swift_code',
      required: false,
    },
  },
  de: {
    nameOfBeneficiary: {
      type: inputTypes.FREE,
      transKey: 'name_of_beneficiary',
      required: true,
    },
    beneficiaryAddress: {
      type: inputTypes.FREE,
      transKey: 'beneficiarys_address',
      required: true,
    },
    bankName: {
      type: inputTypes.FREE,
      transKey: 'bank_name',
      required: true,
    },
    bankAddress: {
      type: inputTypes.FREE,
      transKey: 'bank_address',
      required: true,
    },
    bicOrSwiftCode: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'bic_swift_code',
      required: true,
    },
    ibanNumber: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'iban_number',
      required: true,
    },

    accountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'account_number',
      required: false,
    },
  },
  au: {
    recipientsFullName: {
      type: inputTypes.FREE,
      transKey: 'recipient_full_name',
      required: true,
      max: 250,
    },
    accountName: {
      type: inputTypes.FREE,
      transKey: 'account_name',
      required: true,
      min: 2,
      max: 250,
    },
    streetAddress: {
      type: inputTypes.FREE,
      transKey: 'street_address',
      required: true,
      max: 300,
    },
    bsb: {
      type: inputTypes.NUMBER,
      transKey: 'bsb',
      required: true,
      min: 6,
      max: 6,
    },
    accountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'account_number',
      required: true,
      min: 6,
      max: 9,
    },
    swiftCode: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'swift_code',
      required: false,
      min: 8,
      max: 11,
    },
  },
  nz: {
    accountHolderName: {
      type: inputTypes.FREE,
      transKey: 'account_holder_name',
      required: true,
      max: 250,
    },
    address: {
      type: inputTypes.FREE,
      transKey: 'address',
      required: true,
    },
    bankName: {
      type: inputTypes.NO_NUMBER,
      transKey: 'bank_name',
      required: true,
      max: 100,
    },
    branchName: {
      type: inputTypes.FREE,
      transKey: 'branch_name',
      required: true,
      max: 100,
    },
    branchAddress: {
      type: inputTypes.FREE,
      transKey: 'branch_address',
      required: true,
      max: 100,
    },
    accountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'account_number',
      required: true,
      min: 15,
      max: 16,
    },
    swiftCode: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'swift_code',
      required: false,
      min: 8,
      max: 11,
    },
  },
  us: {
    nameOfBeneficiary: {
      type: inputTypes.NUMBER_LETTER_SPACE,
      transKey: 'name_of_beneficiary',
      required: true,
    },
    accountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'account_number',
      required: true,
    },
    routingNumber: {
      type: inputTypes.NUMBER,
      transKey: 'routing_number',
      required: true,
    },
    bicOrSwiftCode: {
      type: inputTypes.NUMBER_LETTER,
      transKey: 'bic_swift_code',
      required: false,
    },
    bankName: {
      type: inputTypes.NUMBER_LETTER_SPACE,
      transKey: 'bank_name',
      required: true,
    },
  },
  hk: {
    beneficiaryBankName: {
      type: inputTypes.FREE,
      transKey: 'beneficiarys_bank_name',
      required: true,
    },
    bankAddress: {
      type: inputTypes.FREE,
      transKey: 'bank_address',
      required: true,
    },
    beneficiaryName: {
      type: inputTypes.FREE,
      transKey: 'beneficiarys_name',
      required: true,
    },
    beneficiaryAddr: {
      type: inputTypes.FREE,
      transKey: 'beneficiarys_address',
      required: true,
    },
    beneficiaryAccountNumber: {
      type: inputTypes.NUMBER,
      transKey: 'beneficiarys_account_number',
      required: true,
    },
    bankCode: {
      type: inputTypes.NUMBER,
      transKey: 'bank_code',
      required: false,
    },
    swiftCode: {
      type: inputTypes.LETTER,
      transKey: 'swift_code',
      required: true,
    },
  },
  Other: {
    accountNumber: {
      type: inputTypes.FREE,
      transKey: 'account_number',
      required: true,
    },
    nameOfBeneficiary: {
      type: inputTypes.FREE,
      transKey: 'name_of_beneficiary',
      required: true,
    },
    beneficiaryAddress: {
      type: inputTypes.FREE,
      transKey: 'beneficiarys_address',
      required: false,
    },
    bankName: {
      type: inputTypes.FREE,
      transKey: 'bank_name',
      required: false,
    },
    ibanNumber: {
      type: inputTypes.FREE,
      transKey: 'iban_number',
      required: false,
    },
    routingNumber: {
      type: inputTypes.FREE,
      transKey: 'routing_number',
      required: false,
    },
    sortCode: {
      type: inputTypes.FREE,
      transKey: 'sort_code',
      required: false,
    },
    bicOrSwiftCode: {
      type: inputTypes.FREE,
      transKey: 'bic_swift_code',
      required: false,
    },
  },
};

export default class BankTransfer extends React.Component {
  constructor(props) {
    super(props);

    this.fields = this.getFields(props.country);
  }

  validateFields = callback => {
    this.props.form.validateFields(Object.keys(this.fields), callback);
  };

  isFieldsTouched = () => this.props.form.isFieldsTouched(Object.keys(this.fields));

  getFields = country => {
    const fields = bankTransferFields[country];
    if (fields) {
      return fields;
    }
    return bankTransferFields.Other;
  };

  getFieldRules = field => {
    const rules = [];
    const { t } = this.props;
    const fieldTitle = t(`landlord.withdrawal.create.bank_transfer.${field.transKey}`);

    // requied
    if (field.required) {
      rules.push({
        required: true,
        message: t('landlord.withdrawal.create.bank_transfer.common_error', { field: fieldTitle }),
      });
    }

    // type: number / number_letter / free
    if (field.type === inputTypes.NUMBER) {
      rules.push({
        pattern: /^[0-9]+$/,
        message: t('landlord.withdrawal.create.bank_transfer.number_error'),
      });
    } else if (field.type === inputTypes.LETTER) {
      rules.push({
        pattern: /^[A-Za-z]+$/,
        message: t('landlord.withdrawal.create.bank_transfer.letter_error'),
      });
    } else if (field.type === inputTypes.NUMBER_LETTER) {
      rules.push({
        pattern: /^[0-9A-Za-z]+$/,
        message: t('landlord.withdrawal.create.bank_transfer.number_letter_error'),
      });
    } else if (field.type === inputTypes.NUMBER_LETTER_SPACE) {
      rules.push({
        pattern: /^[0-9A-Za-z\s]+$/,
        message: t('landlord.withdrawal.create.bank_transfer.number_letter_space_error'),
      });
    } else if (field.type === inputTypes.NO_NUMBER) {
      rules.push({
        pattern: /^\D+$/,
        message: t('landlord.withdrawal.create.bank_transfer.no_number_error'),
      });
    }

    // length limit
    if (field.min) {
      rules.push({
        min: field.min,
        message: t(
          `landlord.withdrawal.create.bank_transfer.${
            field.type === inputTypes.NUMBER ? 'number_error' : 'number_letter_error'
          }.min`,
          {
            number: field.min,
          },
        ),
      });
    }
    if (field.max) {
      rules.push({
        max: field.max,
        message: t(
          `landlord.withdrawal.create.bank_transfer.${
            field.type === inputTypes.NUMBER ? 'number_error' : 'number_letter_error'
          }.max`,
          {
            number: field.max,
          },
        ),
      });
    } else {
      rules.push({
        max: 255,
        message: t(
          `landlord.withdrawal.create.bank_transfer.${
            field.type === inputTypes.NUMBER ? 'number_error' : 'number_letter_error'
          }.max`,
          {
            number: 255,
          },
        ),
      });
    }

    return rules;
  };

  render() {
    const {
      t,
      data,
      form: { getFieldDecorator },
      desc,
    } = this.props;

    return (
      <div>
        <div className="withdrawal-edit__method-desc">
          {getSafeHtml(desc ? desc : t('landlord.withdrawal.create.bank_transfer.desc'))}
        </div>
        <Row gutter={16}>
          {Object.keys(this.fields).map(fieldName => {
            const field = this.fields[fieldName];
            const fieldTitle = t(`landlord.withdrawal.create.bank_transfer.${field.transKey}`);
            return (
              <Col span={12} key={fieldName}>
                <Form.Item label={fieldTitle} required={field.required}>
                  {getFieldDecorator(fieldName, {
                    initialValue: data[fieldName],
                    validateTrigger: 'onBlur',
                    rules: this.getFieldRules(field),
                  })(
                    <Input
                      placeholder={t(
                        'landlord.withdrawal.create.bank_transfer.common_placeholder',
                        { field: fieldTitle },
                      )}
                      autoComplete="off"
                    />,
                  )}
                </Form.Item>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }
}

BankTransfer.propTypes = {
  t: PropTypes.func,
  country: PropTypes.string,
  desc: PropTypes.string,
  data: PropTypes.object,
  form: PropTypes.object.isRequired,
};

BankTransfer.defaultProps = {
  t: () => {},
  country: '',
  desc: '',
  data: {},
  form: {
    getFieldDecorator: () => {},
  },
};
